import useRecentSearchesTopicPicker from 'components/Common/TopicPicker/useRecentSearchesTopicPicker';
import { useMainSectionParameter } from 'components/Dashboard/helpers/useMainSectionParameter';
import useOutsideHoverElement from 'components/Dashboard/hooks/useOutsideHoverElement';
import useSearchBarUiFunctions from 'components/Dashboard/hooks/useSearchBarUiFunctions';
import useTopicOrClientWord from 'components/Dashboard/hooks/useTopicOrClientWord';
import useDetectRateMode from 'components/Dashboard/ListControl/useDetectRateMode';
import { InputComponent, useInputSearchResultsFunction } from 'components/Dashboard/Navigation/SimpleSearchBar';
import preventLink from 'components/Dashboard/TagsList/prevenLink';
import getUrlParam from 'components/Dashboard/utilities/getUrlParam';
import SortArray from 'components/Dashboard/utilities/SortArray';
import useIsMobile from 'components/Dashboard/utilities/useIsMobile';
import { useCloseButtonFunctionality } from 'components/SavedItems/ui/SavedItemsIcon';
import { store } from 'components/Store';
import { useContext, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Portal } from 'react-portal';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { getOS } from 'utils/getOS';

const TopNavSearch = ({ mainSearch }) => {
  const globalState = useContext(store);
  const { dispatch } = globalState;
  const { closeButtonFunctionality } = useCloseButtonFunctionality();
  const searchParam = getUrlParam('search');
  const orParam = getUrlParam('or');
  const [searchActive, setSearchActive] = useState(!!searchParam);
  const [showDropdownContent, setShowDropdownContent] = useState(false);

  const { searchResultsSection } = useMainSectionParameter();
  const location = useLocation();
  const navigate = useNavigate();

  const topicContainer = useRef(null);
  const inputSearch = useRef(null);
  const scrollContainerElement = useRef(null);
  const topicsListElement = useRef(null);
  const keywordListsElement = useRef(null);
  const isMobile = useIsMobile();
  const smallDesktop = useIsMobile(1300);

  const { startMoveLists, listPosition, setStartMoveLists, setListPosition, scrollList } = useSearchBarUiFunctions({
    inputSearch,
    keywordListsElement,
    scrollContainerElement,
    topicsListElement,
    showDropdownContent,
  });
  const { newAgencyPlan } = useTopicOrClientWord();
  const { rateUpdatesMode } = useDetectRateMode();
  const searchBarOnlyIconCondition = isMobile || rateUpdatesMode || (newAgencyPlan && !mainSearch);
  const inactiveState = !searchActive && !searchParam && !showDropdownContent;

  useEffect(() => {
    setSearchActive(!!searchParam);
  }, [searchParam]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        if (showDropdownContent) {
          setShowDropdownContent(false);
          document.querySelector('.searchbar-with-topics')?.classList.add('z-extra-max');
        } else {
          handleSearch();
        }
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [showDropdownContent]);

  useEffect(() => {
    window.addEventListener('resize', hideSearchBar);
    return () => window.removeEventListener('resize', hideSearchBar);
  }, []);

  useEffect(() => {
    if (!!orParam) {
      setSearchActive(false);
    }
  }, [orParam]);

  const handleSearch = () => {
    if (searchBarOnlyIconCondition) {
      let prevUrl = `${location.pathname}${location.search}${location.hash}`;
      dispatch({
        type: 'MODIFY_SECTION',
        parameter: 'prevUrl',
        value: prevUrl,
      });
      navigate(`/search-results`);
    } else {
      setSearchActive(true);
      inputSearch.current?.focus();
      closeButtonFunctionality();
      setShowDropdownContent(true);
      document.querySelector('.searchbar-with-topics')?.classList.remove('z-extra-max');
    }
  };

  const hideSearchBar = () => {
    setShowDropdownContent(false);
    setSearchActive(false);
    inputSearch.current?.blur();
    document.querySelector('.searchbar-with-topics')?.classList.add('z-extra-max');
  };

  const { recentSearches, setRecentSearches } = useRecentSearchesTopicPicker();
  const { searchResults } = useInputSearchResultsFunction({ closeFunction: hideSearchBar });
  const seen = new Set();
  const searchesOrdered = recentSearches
    .sort(SortArray('date', 'desc'))
    ?.filter((item) => {
      if (seen.has(item.value)) return false;
      seen.add(item.value);
      return true;
    })
    .slice(0, 5);

  const unselectSearch = () => {
    setStartMoveLists(false);
    setListPosition(0);
  };
  useOutsideHoverElement(keywordListsElement, unselectSearch);

  const [positionStyles, setPositionStyles] = useState({});

  useLayoutEffect(() => {
    const updatePositionStyles = () => {
      if (topicContainer?.current) {
        const rect = topicContainer.current.getBoundingClientRect();
        setPositionStyles({
          position: 'fixed',
          top: rect.bottom,
          left: rect.left,
          width: rect.width,
        });
      }
    };

    updatePositionStyles();
    window.addEventListener('resize', updatePositionStyles);

    return () => {
      window.removeEventListener('resize', updatePositionStyles);
    };
  }, [topicContainer, showDropdownContent]);

  return (
    <>
      {showDropdownContent && (
        <div className='general-close-background adjustment-of-position' onClick={hideSearchBar}></div>
      )}
      <div
        className={`${
          mainSearch ? 'top-nav-search-main-search' : 'mr-lg-3 dropdown-keywords-lists'
        } flex-centered nowrap-item flex-centered ${
          searchBarOnlyIconCondition
            ? `simple-searchbar ${searchResultsSection ? 'simple-searchbar-active' : ''}`
            : `light-60-text ${
                mainSearch ? '' : 'bg-main-white'
              } specific-y-padding-5 specific-x-padding-15 justify-content-between adjustment-of-position position-relative  ${
                showDropdownContent ? 'dropdown-keywords-lists-open' : 'rounded-pill border-transparent'
              }`
        }`}
        onClick={handleSearch}
        ref={topicContainer}
        style={{ minWidth: 'auto' }}
      >
        <div className='flex-centered flex-grow-1'>
          <span className={`icon-search-medium`} />
          {!searchBarOnlyIconCondition && (
            <>
              <div className='pl-2 flex-grow-1 flex-centered '>
                <InputComponent
                  ref={inputSearch}
                  openSearchBar={true}
                  preventInitialFocus={!newAgencyPlan}
                  placeholder={mainSearch ? 'Search PolicyMogul' : `Search ${smallDesktop ? '' : 'PolicyMogul'}`}
                  onBlur={() => {
                    setSearchActive(false);
                    setStartMoveLists(false);
                  }}
                  closeFunction={hideSearchBar}
                  inactiveState={inactiveState}
                  setRecentSearches={setRecentSearches}
                />
              </div>
              {inactiveState && !newAgencyPlan && (
                <span className='d-none search-bar-shortcut light-60-text'>
                  {getOS() === 'MacOS' ? (
                    <span className='icon-command  d-inline-block-centered mr-1 paragraph-p3 centered-2' />
                  ) : (
                    'Ctrl'
                  )}
                  + K
                </span>
              )}
            </>
          )}
        </div>
      </div>
      {!searchBarOnlyIconCondition && (
        <Portal>
          <div
            className={`open-dropdown d-${
              showDropdownContent ? 'block' : 'none'
            } dropdown-fixed-position adjustment-of-position`}
            style={positionStyles}
          >
            <div className='open-dropdown-container'>
              <div
                ref={topicsListElement}
                onKeyDown={(e) => {
                  scrollList(e);
                }}
              >
                <div className='scroll-container hover-container search-bar-lists' ref={scrollContainerElement}>
                  <ul className='policy-list px-0 mb-0 hover-content' ref={keywordListsElement}>
                    {searchesOrdered?.length > 0 && (
                      <>
                        {searchesOrdered?.map((item, index) => {
                          return (
                            <RecentSearchItem
                              key={`recentSearch${index}`}
                              item={item}
                              searchResults={searchResults}
                              startMoveLists={startMoveLists}
                              listPosition={listPosition}
                              setStartMoveLists={setStartMoveLists}
                              setListPosition={setListPosition}
                              recentSearches={recentSearches}
                              setRecentSearches={setRecentSearches}
                              index={index}
                            />
                          );
                        })}
                      </>
                    )}
                  </ul>
                </div>
                <div className='border-top specific-y-padding-5 rounded-pill-20-bottom'>
                  <Link
                    to={
                      searchResultsSection
                        ? { search: `${location?.search}${!!location?.search ? '&' : '?'}open=true` }
                        : `/search-results?open=true`
                    }
                    className='text-main-blue flex-centered text-left general-button px-3 specific-y-padding-8 action-state-hover reset-link text-decoration-none general-link '
                    onClick={hideSearchBar}
                  >
                    <span className='icon-filters mr-2' />
                    <span className='text-nowrap'>
                      Advanced <span className='d-none top-nav-actions-words'>search</span>
                    </span>
                  </Link>
                  {!newAgencyPlan && (
                    <Link
                      to={`/search-results`}
                      className='text-main-blue flex-centered text-left general-button px-3 specific-y-padding-8 action-state-hover reset-link text-decoration-none general-link '
                      onClick={() => {
                        hideSearchBar();
                      }}
                    >
                      <span className='icon-description-thin paragraph-p6 mr-2 d-inline-block-centered specific-top-margin-1' />
                      <span className='text-nowrap'>
                        <span className='d-none top-nav-actions-words'>Explore</span>{' '}
                        <span className='all-updates-word'>all</span> updates
                      </span>
                    </Link>
                  )}
                </div>
              </div>
            </div>
          </div>
        </Portal>
      )}
    </>
  );
};

const RecentSearchItem = ({
  item,
  searchResults,
  startMoveLists,
  listPosition,
  setStartMoveLists = () => {},
  setListPosition = () => {},
  recentSearches,
  setRecentSearches,
  index,
  selected,
}) => {
  const isMobile = useIsMobile();
  const { value } = item;
  const [hoveredItem, setHoveredItem] = useState(false);
  return (
    <div
      key={`recentSearch${index}`}
      className={`${startMoveLists && listPosition === index ? 'dropdown-keywordlist-highlighted' : ''} ${
        typeof listPosition === 'undefined' ? 'hover-bg-blue' : ''
      }`}
      onClick={() => {
        searchResults(value);
      }}
      onMouseOver={(e) => {
        if (!isMobile) {
          if (e.target.closest('.top-nav-close-button')) {
            setStartMoveLists(false);
          } else {
            if (!startMoveLists) {
              setStartMoveLists(true);
            }
          }
          setListPosition(index);
          setHoveredItem(true);
        }
      }}
      onMouseLeave={() => {
        setHoveredItem(false);
      }}
    >
      <div className='dropdown-keywordlist flex-centered py-2 px-3 light-50-text'>
        <div className='flex-centered flex-grow-1 hidden-lines hidden-one-line'>
          <span className='icon-clock specific-right-margin-12  d-inline-block-centered mt-0' />
          <span>{value}</span>
        </div>
        <button
          className='general-button circle-button circle-button-30 action-state-hover flex-centered justify-content-center top-nav-close-button'
          onClick={(e) => {
            preventLink(e);
            const updatedSearches = recentSearches.filter((item) => item.value !== value);
            setRecentSearches(updatedSearches);
          }}
        >
          {!hoveredItem && selected ? (
            <span className='icon-tick topic-selected-mark text-main-blue paragraph-p1 d-inline-block' />
          ) : (
            <span className='icon-close paragraph-p6 light-50-text specific-top-margin-2' />
          )}
        </button>
      </div>
    </div>
  );
};

export { RecentSearchItem };
export default TopNavSearch;
